import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Calendar, Camera, Users, LogIn } from 'lucide-react'
import { Helmet } from 'react-helmet-async'

export default function HomePage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (isClient && !isLoading && isAuthenticated) {
      // Only redirect authenticated users to dashboard
      navigate('/dashboard')
    }
  }, [isAuthenticated, isLoading, navigate, isClient])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/fwo-logo.png" 
            alt="FlyWideOpen" 
            className="h-16 mx-auto mb-4 opacity-50 animate-pulse" 
          />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Determine gallery URL based on environment
  const galleryUrl = window.location.hostname.includes('dev') 
    ? 'https://gallery.dev.flywideopen.com'
    : 'https://gallery.flywideopen.com'

  return (
    <>
      <Helmet>
        <title>FWO Pilot Portal</title>
        <meta name="description" content="FlyWideOpen Pilot Portal - Competition management for paragliding pilots" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <img 
                  src="/fwo-logo.png" 
                  alt="FlyWideOpen" 
                  className="h-10" 
                />
                <h1 className="text-xl font-bold text-gray-900">Pilot Portal</h1>
              </div>
              <nav className="flex items-center space-x-8">
                {isAuthenticated ? (
                  <div className="hidden md:flex space-x-8">
                    <Link to="/dashboard" className="text-gray-600 hover:text-black transition-colors">Dashboard</Link>
                    <Link to="/events" className="text-gray-600 hover:text-black transition-colors">Events</Link>
                    <Link to="/photos" className="text-gray-600 hover:text-black transition-colors">Photos</Link>
                    <Link to="/membership" className="text-gray-600 hover:text-black transition-colors">Profile</Link>
                  </div>
                ) : (
                  <Link 
                    to="/login" 
                    className="inline-flex items-center px-4 py-2 bg-fwo-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Login
                  </Link>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Section */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Welcome to FlyWideOpen
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your competition portal for events, photo competitions, and pilot management
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 max-w-4xl mx-auto">
            <FeatureCard
              icon={<Calendar className="h-8 w-8" />}
              title="Event Schedule"
              description="View upcoming talks, sessions, and bus schedules"
              color="bg-fwo-blue"
            />
            <FeatureCard
              icon={<Camera className="h-8 w-8" />}
              title="Photo Competition"
              description="Submit daily themed photos and view galleries"
              color="bg-blue-700"
              action={!isAuthenticated ? {
                label: "View Gallery",
                href: galleryUrl,
                external: true
              } : undefined}
            />
            <FeatureCard
              icon={<Users className="h-8 w-8" />}
              title="Pilot Community"
              description="Connect with other competition participants"
              color="bg-blue-600"
            />
          </div>

          {/* Call to Action */}
          {!isAuthenticated && (
            <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center border border-gray-200">
              <img 
                src="/fwo-logo.png" 
                alt="FlyWideOpen" 
                className="h-16 mx-auto mb-6" 
              />
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Ready to Get Started?
              </h3>
              <p className="text-gray-600 mb-6">
                Access your competition portal with your registered pilot credentials.
              </p>
              <Link 
                to="/login"
                className="inline-flex items-center px-6 py-3 bg-fwo-blue text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:ring-offset-2 transition-colors"
              >
                <LogIn className="h-5 w-5 mr-2" />
                Login to Portal
              </Link>
              <p className="mt-4 text-sm text-gray-500">
                Only registered pilots from Airtribune can access this portal.
              </p>
            </div>
          )}

          {isAuthenticated && (
            <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center border border-gray-200">
              <img 
                src="/fwo-logo.png" 
                alt="FlyWideOpen" 
                className="h-16 mx-auto mb-6" 
              />
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Welcome Back!
              </h3>
              <p className="text-gray-600 mb-6">
                You're logged in and ready to access your competition portal.
              </p>
              <Link 
                to="/dashboard"
                className="inline-flex items-center px-6 py-3 bg-fwo-blue text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:ring-offset-2 transition-colors"
              >
                Go to Dashboard
              </Link>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center text-gray-600">
              <p>&copy; {new Date().getFullYear()} FlyWideOpen. All rights reserved.</p>
              <p className="mt-2 text-sm">
                Paragliding competition management platform
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
  color: string
  action?: {
    label: string
    href: string
    external?: boolean
  }
}

function FeatureCard({ icon, title, description, color, action }: FeatureCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
      <div className={`${color} text-white p-4 rounded-xl inline-block mb-6`}>
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
      {action && (
        <a
          href={action.href}
          target={action.external ? "_blank" : undefined}
          rel={action.external ? "noopener noreferrer" : undefined}
          className="inline-block mt-4 px-4 py-2 bg-fwo-blue text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          {action.label}
        </a>
      )}
    </div>
  )
}